import { useState } from 'react';
import { usePresence } from '../../state/presence';
import { useSession } from '../../state/session';
import './AvatarStack.css';

const MAX_SHOWN = 4;

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * Who else is in the room. Shows the local user first, then peers, collapsing
 * past MAX_SHOWN into a +N chip.
 *
 * Anonymous visitors arrive with an auto-assigned "Guest NNNN" name, so this
 * doubles as the place to rename yourself — better than a modal in front of an
 * editor someone just clicked into.
 */
export default function AvatarStack() {
  const peers = usePresence((s) => s.peers);
  const self = usePresence((s) => s.self);
  const setDisplayName = usePresence((s) => s.setDisplayName);
  const signedIn = useSession((s) => s.status === 'authenticated');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!self) return null;

  const shown = peers.slice(0, MAX_SHOWN);
  const overflow = peers.length - shown.length;

  function beginRename() {
    if (signedIn || !self) return; // your account name is your name
    setDraft(self.name);
    setEditing(true);
  }

  function commitRename() {
    setDisplayName(draft);
    setEditing(false);
  }

  return (
    <ul className="avatars" aria-label="Collaborators">
      <li className="avatar-self-slot">
        {editing ? (
          <input
            className="avatars-rename"
            aria-label="Your display name"
            autoFocus
            value={draft}
            maxLength={24}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setEditing(false);
            }}
          />
        ) : (
          // A real button, not a span with onClick: the span was mouse-only.
          <button
            type="button"
            className="avatar avatar-self"
            style={{ background: self.color }}
            title={signedIn ? `${self.name} (you)` : `${self.name} (you) — click to rename`}
            aria-label={signedIn ? `${self.name} (you)` : `${self.name} (you), rename yourself`}
            disabled={signedIn}
            onClick={beginRename}
          >
            {initials(self.name)}
          </button>
        )}
      </li>

      {shown.map((peer) => (
        <li
          key={peer.clientId}
          className="avatar"
          style={{ background: peer.color }}
          title={peer.name}
          aria-label={peer.name}
        >
          {initials(peer.name)}
        </li>
      ))}

      {overflow > 0 && (
        <li
          className="avatar avatar-more"
          title={peers.map((p) => p.name).join(', ')}
          aria-label={`${overflow} more: ${peers.map((p) => p.name).join(', ')}`}
        >
          +{overflow}
        </li>
      )}
    </ul>
  );
}
